import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getAssistantList } from "../utility/getAssistantList";
import { CreateNewBotDialog } from "./CreateNewBotDialog";
import { deleteAssistant } from "../utility/deleteAssistant";
import { t } from "framer-motion/dist/types.d-CQt5spQA";

type assistantType = {
  name: string;
  instructions: string;
  id: string;
};

type SelectBotDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  applyAssistant: (assistant: assistantType) => void;
};

export const SelectBotDialog: React.FC<SelectBotDialogProps> = ({
  isOpen,
  setIsOpen,
  applyAssistant,
}) => {
  const { idToken, refreshAuthSession } = useAuth();
  const [assistantList, setAssistantList] = useState<assistantType[]>([]);
  const [selectedAssistantId, setSelectedAssistantId] = useState<string>("");

  const [createNewBotDialogIsOpen, setCreateNewBotDialogIsOpen] =
    useState<boolean>(false);

  const [deleteConfirmDialogIsOn, setDeleteConfirmDialogIsOn] =
    useState<boolean>(false);

  const [reloadAssistantList, setReloadAssistantList] =
    useState<boolean>(false);

  useEffect(() => {
    if (!idToken) return;
    getAssistantList(idToken).then((data) => {
      // 取得したボットのリストを処理する
      setAssistantList(data.assistants);
    });
    setReloadAssistantList(false);
  }, [idToken, reloadAssistantList]);

  const handleSelectChange = (event: SelectChangeEvent) => {
    setSelectedAssistantId(event.target.value);
  };

  const handleSelect = () => {
    const selectedAssistant = assistantList.find(
      (assistant) => assistant.id === selectedAssistantId
    );
    if (selectedAssistant && applyAssistant) {
      applyAssistant(selectedAssistant);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleDelete = () => {
    if (!idToken) return;
    if (
      selectedAssistantId === "asst_G30B0iSqmWBNOgrjRIIFnNpd" ||
      selectedAssistantId === "asst_G81W5qlBs9HN5uzPIomlKVNH"
    )
      return;

    deleteAssistant(idToken, selectedAssistantId).then(() => {
      setReloadAssistantList(true);
    });

    setDeleteConfirmDialogIsOn(false);
    setSelectedAssistantId("");
  };

  if (assistantList.length === 0) return null;

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth>
      <DialogTitle>BOTの選択</DialogTitle>
      <DialogContent>
        <Button
          variant="contained"
          onClick={() => setCreateNewBotDialogIsOpen(true)}
        >
          ＋ 新規作成
        </Button>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>ボットを選択</InputLabel>
          <Select
            value={selectedAssistantId}
            label="ボットを選択"
            onChange={handleSelectChange}
          >
            {assistantList.map((assistant) => (
              <MenuItem key={assistant.id} value={assistant.id}>
                {assistant.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedAssistantId && (
          <div style={{ marginTop: 16 }}>
            <h4>説明:</h4>
            <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
              {
                assistantList.find((a) => a.id === selectedAssistantId)
                  ?.instructions
              }
            </Typography>
          </div>
        )}
        {selectedAssistantId && (
          <Button
            variant="outlined"
            color="error"
            disabled={
              selectedAssistantId === "asst_G30B0iSqmWBNOgrjRIIFnNpd" ||
              selectedAssistantId === "asst_G81W5qlBs9HN5uzPIomlKVNH"
            }
            onClick={() => setDeleteConfirmDialogIsOn(true)}
          >
            このBOTを削除する
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>キャンセル</Button>
        <Button
          onClick={handleSelect}
          disabled={!selectedAssistantId}
          variant="contained"
        >
          選択
        </Button>
      </DialogActions>
      <CreateNewBotDialog
        isOpen={createNewBotDialogIsOpen}
        setIsOpen={setCreateNewBotDialogIsOpen}
        setReload={setReloadAssistantList}
      />

      <Dialog
        open={deleteConfirmDialogIsOn}
        onClose={() => setDeleteConfirmDialogIsOn(false)}
      >
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <Typography>このBOTを削除してもよろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialogIsOn(false)}>
            キャンセル
          </Button>
          <Button onClick={handleDelete} color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
